// Phần thuần của khối cây mạng lưới (dải thống kê, bộ lọc, cuộn tới chỗ vừa mở) — dùng chung cho /npp/network
// và /network (Đại lý / CTV). Dựng cây, trải hàng, xếp ô ở @shoptrack/common/network-tree / network-drill — ở đây
// không dựng lại.

import {
  networkStatusLabel,
  type NetworkCounts, type NetworkIssue, type NetworkTree, type NetworkUnitBase, type NetworkUnitType,
} from '@shoptrack/common/network-tree'

/** Bộ lọc cây: tất cả, cần xử lý, hoặc một loại đơn vị (bấm ô loại trên dải thống kê). */
export type NetworkFilter = 'all' | 'issues' | NetworkUnitType

export interface NetworkStripCounts {
  counts: NetworkCounts
  /** Shop có ít nhất một đơn trong 30 ngày. */
  shopsWithOrders: number
  /** Tổng đơn 30 ngày của mọi đơn vị trong cây. */
  orders30d: number
  /** Số đơn vị có việc cần xử lý. */
  issues: number
}

/** Số liệu dải thống kê trên cây — đếm trên cả cây, không theo nhánh đang mở hay bộ lọc. */
export function networkStripCounts<U extends NetworkUnitBase & { orders30d?: number | null }>(
  tree: NetworkTree<U>,
  hasIssue: (unit: U) => boolean,
): NetworkStripCounts {
  let shopsWithOrders = 0
  let orders30d = 0
  let issues = 0
  for (const { unit } of tree.byId.values()) {
    // API cũ chưa trả số đơn → coi như 0, không ra NaN
    const n = Number(unit.orders30d) || 0
    orders30d += n
    if (unit.tenantType === 'shop' && n > 0) shopsWithOrders += 1
    if (hasIssue(unit)) issues += 1
  }
  return { counts: { ...tree.counts }, shopsWithOrders, orders30d, issues }
}

/**
 * Gộp ô tìm + bộ lọc thành một hàm khớp cho networkRows / networkLayout. Lọc theo loại cùng nghĩa với tìm: chỉ hiện
 * đơn vị khớp + chuỗi cấp trên. `undefined` = không lọc (cây theo mở / gập).
 */
export function networkFilterMatch<U extends NetworkUnitBase>(
  filter: NetworkFilter,
  text: ((unit: U) => boolean) | undefined,
  hasIssue: (unit: U) => boolean,
): ((unit: U) => boolean) | undefined {
  if (filter === 'all') return text
  const byFilter = filter === 'issues' ? hasIssue : (u: U) => u.tenantType === filter
  return text ? (u: U) => byFilter(u) && text(u) : byFilter
}

/** Bấm lại đúng ô đang lọc = về Tất cả. */
export function nextNetworkFilter(current: NetworkFilter, picked: NetworkFilter): NetworkFilter {
  return current === picked ? 'all' : picked
}

export interface NetworkScrollView {
  left: number
  top: number
  width: number
  height: number
}

export interface NetworkFocusBox {
  x: number
  y: number
  w: number
  h: number
}

/**
 * Vị trí cuộn để khung `box` (cột vừa mở + ô dẫn tới nó, hay ô vừa chọn) nằm trong vùng nhìn, bên trái ngăn chi tiết
 * (`reserveRight` px che mép phải). Khung cao / rộng hơn vùng nhìn thì ưu tiên mép trên / mép trái. Chỉ cuộn khi bị
 * che / khuất — đang thấy thì đứng yên (null), sơ đồ không nhảy mỗi lần bấm.
 */
export function networkFocusScroll(view: NetworkScrollView, box: NetworkFocusBox, reserveRight: number): { left: number; top: number } | null {
  let { left, top } = view
  const viewW = view.width - reserveRight
  if (box.x + box.w + 16 > left + viewW) left = box.x + box.w + 16 - viewW
  if (box.x - 16 < left) left = Math.max(0, box.x - 16)
  if (box.y + box.h + 12 > top + view.height) top = box.y + box.h + 12 - view.height
  if (box.y - 12 < top) top = Math.max(0, box.y - 12)
  return Math.abs(left - view.left) < 1 && Math.abs(top - view.top) < 1 ? null : { left, top }
}

// ─── Cây của Đại lý / CTV (GET /network/units) ────────────────────────────────────────────────────────────────
// API này báo đơn vị đang khoá bằng status 'locked' (lockedAt), không có cờ `locked` riêng như cây của NPP.

/** Nhãn trạng thái của cấp dưới (GET /network/units). */
export function networkDownlineStatusLabel(status: string): string {
  return status === 'locked' ? 'Đang khóa' : networkStatusLabel(status)
}

/**
 * Việc cần xử lý của một cấp dưới: tạm ngưng / đang khoá, chưa có bảng giá, CTV trực tiếp chưa có bảng chiết khấu
 * (`scheme: null` — chỉ CTV mình trả chiết khấu mới có ô này; không có ô = không phải việc của mình).
 */
export function networkDownlineIssues(u: { status: string; priceList: unknown; scheme?: unknown }): NetworkIssue[] {
  const out: NetworkIssue[] = []
  if (u.status !== 'active') {
    out.push({ label: networkDownlineStatusLabel(u.status), severity: u.status === 'suspended' || u.status === 'locked' ? 'danger' : 'warn' })
  }
  if (!u.priceList) out.push({ label: 'Chưa có bảng giá', severity: 'warn' })
  if (u.scheme === null) out.push({ label: 'Chưa có bảng chiết khấu', severity: 'warn' })
  return out
}

/**
 * Cấp dưới trực tiếp (parId = chính mình) treo dưới ô gốc: đổi parId thành null — buildNetworkTree coi cấp trên không
 * có trong danh sách là "mất cấp trên" (orphan), mà chính mình thì không bao giờ có trong danh sách cấp dưới.
 */
export function networkUnderRoot<U extends { parId: string | null }>(units: readonly U[], rootId: string | null | undefined): U[] {
  return units.map((u) => (rootId && u.parId === rootId ? { ...u, parId: null } : u))
}
